// Demo page: NextAdmin /auth/sign-up card frame (AuthLayout), with the /mail-success outcome pattern for the result.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { CheckCircle, Clock, Mail } from 'lucide-react';
import { authApi } from '../services/api/index.js';
import { previewInvitationOptions } from '../generated/api/@tanstack/react-query.gen.js';
import type { InvitationPreview } from '../generated/api/types.gen.js';
import { zAuthSignupCreate } from '../generated/api/zod.gen.js';
import { Form, SubmitButton, TextFormField, useZodForm } from '../components/form/index.js';
import { AuthLayout } from '../components/page/AuthLayout.js';
import { buttonStyles } from '../components/tailgrids/core/button.js';
import { Spinner } from '../components/tailgrids/core/spinner/index.js';

/** The generated signup body, plus the UI-only confirmation field. */
const signupSchema = zAuthSignupCreate
  .pick({ email: true, password: true, username: true })
  .extend({ confirmPassword: z.string() })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match',
  });

const signInLink = (
  <Link to="/login" className="font-medium text-accent-text hover:underline">
    Sign in
  </Link>
);

function Notice({ tone, children }: { tone: 'success' | 'warning' | 'neutral'; children: React.ReactNode }) {
  const Icon = tone === 'success' ? CheckCircle : tone === 'warning' ? Clock : Mail;
  const colours =
    tone === 'success'
      ? 'bg-success-muted text-success-text'
      : tone === 'warning'
        ? 'bg-warning-muted text-warning-text'
        : 'bg-accent/10 text-accent-text';
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span className={`flex size-14 items-center justify-center rounded-full ${colours}`}>
        <Icon size={28} />
      </span>
      {children}
    </div>
  );
}

/**
 * Accounts are created by invitation only (owner, 2026-10-23): the page needs the invitation's `?token=`, and the
 * account takes the invitation's email. The server refuses a sign-up without a valid invitation for that email.
 */
export function SignupPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const preview = useQuery({
    ...previewInvitationOptions({ path: { token } }),
    enabled: token !== '',
    retry: false,
  });

  if (!token) {
    return (
      <AuthLayout
        title="Conflui is invitation-only"
        subtitle="Accounts are created from an invitation. Ask your organization's administrator to invite you."
        footer={<>Already have an account? {signInLink}</>}
      >
        <Notice tone="neutral">
          <p className="text-sm text-body">The invitation email has the link to create your account.</p>
        </Notice>
      </AuthLayout>
    );
  }

  if (preview.isError) {
    return (
      <AuthLayout
        title="This invitation is not valid"
        subtitle="It may have expired or already been used. Ask the person who invited you to send a new one."
      >
        <Link to="/login" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (!preview.data) {
    return (
      <AuthLayout title="Loading your invitation">
        <div className="flex justify-center text-text-tertiary" role="status">
          <Spinner size="md" />
        </div>
      </AuthLayout>
    );
  }

  return <InvitedSignup token={token} invitation={preview.data} />;
}

function InvitedSignup({ token, invitation }: { token: string; invitation: InvitationPreview }) {
  const [created, setCreated] = useState<'approved' | 'pending' | null>(null);
  const form = useZodForm(signupSchema, {
    defaultValues: { email: invitation.email, password: '', confirmPassword: '', username: '' },
  });

  // A valid invitation approves the account and adds it to the inviting organization in the same request.
  const createAccount = async ({ password, username }: z.output<typeof signupSchema>) => {
    const { user } = await authApi.signup(invitation.email, password, username, token);
    setCreated(user.status === 'approved' ? 'approved' : 'pending');
  };

  if (created === 'approved') {
    return (
      <AuthLayout title="Account created" subtitle={`You are a member of ${invitation.orgName}.`}>
        <Notice tone="success">
          <Link to="/login" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
            Sign in
          </Link>
        </Notice>
      </AuthLayout>
    );
  }

  if (created === 'pending') {
    return (
      <AuthLayout title="Account created" footer={<>Approved already? {signInLink}</>}>
        <Notice tone="warning">
          <p className="text-sm text-body">
            An administrator reviews this account before it can sign in. You will be able to sign in once it is
            approved.
          </p>
        </Notice>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle={`Join ${invitation.orgName} on Conflui`}
      footer={<>Already have an account? {signInLink}</>}
    >
      <Form form={form} onSubmit={createAccount}>
        <TextFormField
          name="email"
          type="email"
          label="Email"
          description="The invitation is for this address."
          autoComplete="email"
          readOnly
        />
        <TextFormField
          name="username"
          label="Username"
          description="Optional. 3–50 characters."
          placeholder="Choose a username"
          autoComplete="username"
        />
        <TextFormField
          name="password"
          type="password"
          label="Password"
          description="At least 8 characters."
          autoComplete="new-password"
        />
        <TextFormField name="confirmPassword" type="password" label="Confirm password" autoComplete="new-password" />
        <SubmitButton size="lg" className="w-full">
          Create account
        </SubmitButton>
      </Form>
    </AuthLayout>
  );
}
